import {
  accountWebsite,
  type AccountDetail,
  type AccountInput,
  type Address,
  type EntityType,
  type PersonnelInput,
} from "@/lib/brokerAccounts";
import {
  EMPTY_ADDRESS,
  isCompleteAddress,
  type AddressValue,
} from "@/lib/address";
import {
  phoneRowErrors,
  toPhoneInputs,
  toPhoneValues,
  type PhoneValue,
} from "@/lib/phone";
import { normalizeRoles, type PersonnelRole } from "@/lib/personnelRoles";
import {
  ACCOUNT_FORM_ADDRESS,
  accountAddress,
  carriedFrom,
  carriedInput,
  parseDecimal,
  type CarriedPerson,
} from "@/lib/accountWrite";
import { spouseSigns } from "@/lib/bondRoster";
import {
  validationFailures,
  validationReasonCopy,
  messageFor,
} from "@/lib/errors";
import type { MaritalStatus } from "@/lib/brokerAccounts";

// The account record page's form model: what the broker edits, whether it is
// dirty, and the PARTIAL PUT a save sends.
//
// Only what CHANGED goes out (plus `legalName`, which the partial PUT always
// requires). An untouched personnel roster or address is never sent, so it can
// never be rewritten. When one does change, it goes out whole, carrying every
// loaded field the page never shows (BRK-S223).

export interface PersonRow {
  /** Stable React key: the person's id, or a local key for an added row. */
  key: string;
  /** Present for a LOADED person, absent for one the broker added. */
  id?: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phones: PhoneValue[];
  roles: PersonnelRole[];
  /** The ownership share as typed (0–100); blank means none recorded. A share
   *  above 0 is what makes someone an OWNER — PMS builds the GIA's signers from
   *  it, never from the `owner` role (BRK-S281, PMS-S606). */
  share: string;
  /** Blank means not recorded — an open question, never "single". */
  maritalStatus: MaritalStatus | "";
  spouse: SpouseChoice;
  /** Everything else a loaded person carries back out untouched (BRK-S223). */
  carried?: CarriedPerson;
}

/**
 * Who a person is married to, as the Contacts tab edits it (BRK-S281).
 *
 * `person` names another ROW by its `key` (a loaded person's key is their id),
 * and the link is held on BOTH rows — PMS reads it from either side, and a
 * one-sided edit is how a person ends up named as two people's spouse, which
 * PMS refuses. `linkSpouse` is the only writer. `new` is someone not on the
 * account yet: PMS creates them from the name (the inline spouse branch).
 */
export type SpouseChoice =
  | { kind: "none" }
  | { kind: "person"; key: string }
  | { kind: "new"; firstName: string; lastName: string; email: string };

export const NO_SPOUSE: SpouseChoice = { kind: "none" };

export interface AccountRecordValues {
  legalName: string;
  dbaName: string;
  ein: string;
  entityType: string;
  industryId: string;
  contractorLicenseNumber: string;
  emailAddress: string;
  website: string;
  brokerId: string;
  yearsInBusiness: string;
  annualRevenues: string;
  numEmployees: string;
  natureOfBusiness: string;
  phones: PhoneValue[];
  legalAddress: AddressValue;
  mailingAddress: AddressValue;
  personnel: PersonRow[];
}

function toAddress(a: Address | null): AddressValue {
  if (!a) return { ...EMPTY_ADDRESS };
  return {
    street: a.street,
    city: a.city,
    state: a.state,
    zip: a.zip,
    ...(a.county ? { county: a.county } : {}),
  };
}

const text = (v: string | number | null | undefined) =>
  v == null ? "" : String(v);

export function valuesFromDetail(d: AccountDetail): AccountRecordValues {
  return {
    legalName: d.legalName,
    dbaName: text(d.dbaName),
    ein: text(d.ein),
    entityType: text(d.entityType),
    industryId: text(d.industryId),
    contractorLicenseNumber: text(d.contractorLicenseNumber),
    emailAddress: text(d.emailAddress),
    website: text(accountWebsite(d)),
    brokerId: text(d.servicingBroker?.id),
    yearsInBusiness: text(d.yearsInBusiness),
    annualRevenues: text(parseDecimal(d.annualRevenues ?? null)),
    numEmployees: text(d.numEmployees),
    natureOfBusiness: text(d.natureOfBusiness),
    phones: toPhoneValues(d.phones),
    legalAddress: toAddress(d.legalAddress),
    mailingAddress: toAddress(d.mailingAddress),
    personnel: d.personnel.map((p) => {
      // Marital status and the spouse are EDITED here now (BRK-S281), so they
      // leave the carried bag and become row state.
      const { maritalStatus, spouseId, ...carried } = carriedFrom(p);
      const linked =
        spouseId && d.personnel.some((o) => o.id === spouseId)
          ? spouseId
          : null;
      return {
        key: p.id,
        id: p.id,
        firstName: p.firstName,
        lastName: p.lastName,
        title: text(p.title),
        email: text(p.email),
        phones: toPhoneValues(p.phones),
        roles: normalizeRoles(p.roles ?? []),
        share: text(parseDecimal(p.ownershipPercentage)),
        maritalStatus: maritalStatus ?? "",
        spouse: linked ? { kind: "person", key: linked } : NO_SPOUSE,
        carried,
      };
    }),
  };
}

/** A new, blank person row. `key` only has to be unique within the form. */
export function newPersonRow(key: string): PersonRow {
  return {
    key,
    firstName: "",
    lastName: "",
    title: "",
    email: "",
    phones: toPhoneValues([]),
    roles: [],
    share: "",
    maritalStatus: "",
    spouse: NO_SPOUSE,
  };
}

/** The share as a number, or null when blank or not a number. */
export function shareOf(row: Pick<PersonRow, "share">): number | null {
  const trimmed = row.share.trim().replace(/%$/, "").trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** An owner holds a share above 0 — PMS's own test (lib/gia-roster.ts). */
export function isOwner(row: Pick<PersonRow, "share">): boolean {
  return (shareOf(row) ?? 0) > 0;
}

/** How a row is named in a message: its name, or its place on the list. */
export function personLabel(row: PersonRow, index: number): string {
  return `${row.firstName} ${row.lastName}`.trim() || `Person ${index + 1}`;
}

/** Clear any `person` link on `rows` that points at one of `keys`. */
function unlinkFrom(rows: PersonRow[], keys: readonly string[]): PersonRow[] {
  return rows.map((r) =>
    r.spouse.kind === "person" && keys.includes(r.spouse.key)
      ? { ...r, spouse: NO_SPOUSE }
      : r,
  );
}

/**
 * Set `key`'s spouse, keeping both halves of every couple in step.
 *
 * Linking Pat to Sam first ends Pat's old marriage and Sam's old marriage (on
 * both sides), then links the two from both sides. Anything else would send
 * PMS a payload naming one person as two people's spouse.
 */
export function linkSpouse(
  rows: readonly PersonRow[],
  key: string,
  choice: SpouseChoice,
): PersonRow[] {
  if (!rows.some((r) => r.key === key)) return [...rows];
  const partnerKey = choice.kind === "person" ? choice.key : null;
  return rows.map((r) => {
    if (r.key === key) return { ...r, spouse: choice };
    if (partnerKey !== null && r.key === partnerKey)
      return { ...r, spouse: { kind: "person", key } };
    // A former partner of either of the two is left unlinked.
    if (
      r.spouse.kind === "person" &&
      (r.spouse.key === key || r.spouse.key === partnerKey)
    )
      return { ...r, spouse: NO_SPOUSE };
    return r;
  });
}

/** Remove a person, ending any marriage the list recorded with them. */
export function removePerson(
  rows: readonly PersonRow[],
  key: string,
): PersonRow[] {
  return unlinkFrom(
    rows.filter((r) => r.key !== key),
    [key],
  );
}

/**
 * Set a marital status. A status that puts no spouse on the agreement ends the
 * spouse link too: the Spouse control disappears with it, and a hidden link
 * that still saved would be a change the broker cannot see.
 */
export function setMaritalStatus(
  rows: readonly PersonRow[],
  key: string,
  status: MaritalStatus | "",
): PersonRow[] {
  const withStatus = rows.map((r) =>
    r.key === key ? { ...r, maritalStatus: status } : r,
  );
  return spouseSigns(status) === true
    ? withStatus
    : linkSpouse(withStatus, key, NO_SPOUSE);
}

/** The people `key` can be linked to: everyone else already SAVED on the
 *  account. A person added in this edit has no id yet, and the account write
 *  can only name a spouse by id — "add someone new" covers them instead. */
export function spouseCandidates(
  rows: readonly PersonRow[],
  key: string,
): PersonRow[] {
  const current = rows.find((r) => r.key === key)?.spouse;
  return rows.filter(
    (r) =>
      r.key !== key &&
      (r.id !== undefined ||
        (current?.kind === "person" && current.key === r.key)),
  );
}

// Compare what the broker can see and edit — never React keys or carried data.
function comparable(v: AccountRecordValues) {
  return {
    ...v,
    phones: v.phones.map(({ number, lineType, isPrimary }) => ({
      number,
      lineType,
      isPrimary,
    })),
    personnel: v.personnel.map(
      ({
        id,
        firstName,
        lastName,
        title,
        email,
        phones,
        roles,
        share,
        maritalStatus,
        spouse,
      }) => ({
        id,
        firstName,
        lastName,
        title,
        email,
        roles,
        share: share.trim(),
        maritalStatus,
        spouse,
        phones: phones.map(({ number, lineType, isPrimary }) => ({
          number,
          lineType,
          isPrimary,
        })),
      }),
    ),
  };
}

function same(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function isDirty(
  initial: AccountRecordValues,
  current: AccountRecordValues,
): boolean {
  return !same(comparable(initial), comparable(current));
}

export interface AccountPatchResult {
  input: AccountInput | null;
  /** Human-readable problems that stop the save. */
  errors: string[];
}

// The partial PUT cannot CLEAR an optional value yet (a blank is dropped and
// the stored value kept, PMS-S551 adds explicit clears), so clearing one is
// refused up front rather than "saving" and silently changing nothing.
const CLEARABLE_LABELS: Partial<Record<keyof AccountRecordValues, string>> = {
  dbaName: "Doing business as (DBA)",
  ein: "FEIN",
  entityType: "Legal entity type",
  industryId: "Industry",
  contractorLicenseNumber: "Contractor license",
  emailAddress: "Business email",
  website: "Website",
  brokerId: "Servicing broker",
  yearsInBusiness: "Years in business",
  annualRevenues: "Annual revenue",
  numEmployees: "Employees",
  natureOfBusiness: "Description of operations",
};

function wholeNumber(
  value: string,
  label: string,
  errors: string[],
): number | undefined {
  const n = Number(value.replace(/,/g, ""));
  if (!Number.isInteger(n) || n < 0) {
    errors.push(`${label} must be a whole number.`);
    return undefined;
  }
  return n;
}

function amount(
  value: string,
  label: string,
  errors: string[],
): number | undefined {
  const n = Number(value.replace(/[$,\s]/g, ""));
  if (!Number.isFinite(n) || n < 0) {
    errors.push(`${label} must be an amount in dollars.`);
    return undefined;
  }
  return n;
}

export function buildAccountPatch(
  initial: AccountRecordValues,
  current: AccountRecordValues,
  detail: AccountDetail,
): AccountPatchResult {
  const errors: string[] = [];
  const legalName = current.legalName.trim();
  if (!legalName) errors.push("Enter the legal business name.");

  const input: AccountInput = { legalName };
  const changed = (k: keyof AccountRecordValues) =>
    !same(initial[k], current[k]);

  for (const key of Object.keys(
    CLEARABLE_LABELS,
  ) as (keyof AccountRecordValues)[]) {
    if (!changed(key)) continue;
    const value = String(current[key]).trim();
    if (!value) {
      errors.push(
        `Clearing ${CLEARABLE_LABELS[key]} isn't supported yet — restore a value or discard the change.`,
      );
      continue;
    }
    switch (key) {
      case "dbaName":
      case "ein":
      case "emailAddress":
      case "website":
      case "natureOfBusiness":
      case "industryId":
      case "contractorLicenseNumber":
        input[key] = value;
        break;
      case "brokerId":
        input.brokerId = value;
        break;
      case "entityType":
        input.entityType = value as EntityType;
        break;
      case "yearsInBusiness":
      case "numEmployees": {
        const n = wholeNumber(value, CLEARABLE_LABELS[key]!, errors);
        if (n !== undefined) input[key] = n;
        break;
      }
      case "annualRevenues": {
        const n = amount(value, CLEARABLE_LABELS[key]!, errors);
        if (n !== undefined) input.annualRevenues = n;
        break;
      }
    }
  }

  if (!same(comparable(initial).phones, comparable(current).phones)) {
    const rowErrors = phoneRowErrors(current.phones).filter(Boolean);
    if (rowErrors.length) errors.push(...(rowErrors as string[]));
    const phones = toPhoneInputs(current.phones);
    if (phones.length) input.phones = phones;
    else errors.push("Keep at least one business phone number.");
  }

  const addresses: [
    keyof AccountRecordValues & ("legalAddress" | "mailingAddress"),
    string,
    Address | null,
  ][] = [
    ["legalAddress", "legal", detail.legalAddress],
    ["mailingAddress", "mailing", detail.mailingAddress],
  ];
  for (const [key, label, loaded] of addresses) {
    if (!changed(key)) continue;
    const value = current[key];
    if (!isCompleteAddress(value)) {
      errors.push(
        `Complete the ${label} address (street, city, state and ZIP).`,
      );
      continue;
    }
    input[key] = accountAddress(value, ACCOUNT_FORM_ADDRESS, loaded);
  }

  if (!same(comparable(initial).personnel, comparable(current).personnel)) {
    const named = current.personnel.filter(
      (p) => p.firstName.trim() || p.lastName.trim(),
    );
    if (named.some((p) => !p.firstName.trim() || !p.lastName.trim())) {
      errors.push("Every person needs a first and a last name.");
    }
    if (named.length === 0)
      errors.push("Keep at least one person on the account.");
    const rosterIds = new Set(named.flatMap((p) => (p.id ? [p.id] : [])));
    const byKey = new Map(named.map((p) => [p.key, p]));
    let shareTotal = 0;
    const people: PersonnelInput[] = named.map((p, i) => {
      const who = personLabel(p, i);
      const phones = toPhoneInputs(p.phones);
      const share = shareOf(p);
      if (
        p.share.trim() !== "" &&
        (share === null || share < 0 || share > 100)
      ) {
        errors.push(`${who}: enter an ownership share from 0 to 100.`);
      }
      if (share !== null && share >= 0 && share <= 100) shareTotal += share;
      return {
        ...(p.id ? { id: p.id } : {}),
        firstName: p.firstName.trim(),
        lastName: p.lastName.trim(),
        ...(p.title.trim() ? { title: p.title.trim() } : {}),
        ...(p.email.trim() ? { email: p.email.trim() } : {}),
        ...(phones.length ? { phones } : {}),
        ...(p.roles.length ? { roles: p.roles } : {}),
        // A blank share clears it: the roster write is wholesale, so an
        // omitted field is "none recorded".
        ...(share !== null && share >= 0 && share <= 100
          ? { ownershipPercentage: share }
          : {}),
        ...carriedInput(p.carried, rosterIds),
        ...(p.maritalStatus ? { maritalStatus: p.maritalStatus } : {}),
        ...spouseInput(p, who, byKey, errors),
      };
    });
    if (shareTotal > 100) {
      errors.push("Ownership shares can’t add up to more than 100%.");
    }
    input.personnel = people;
  }

  return { input: errors.length ? null : input, errors };
}

/**
 * A row's spouse on the wire (BRK-S281), or nothing.
 *
 * Linked to another person: `{ personnelId }`, and only when that person is
 * saved and still in this write — the account write names a spouse by id and
 * 400s anyone else. Both halves of a couple state it; PMS reads that as one
 * marriage. Someone new: the inline branch, and PMS creates them.
 */
function spouseInput(
  p: PersonRow,
  who: string,
  byKey: ReadonlyMap<string, PersonRow>,
  errors: string[],
): Pick<PersonnelInput, "spouse"> {
  const s = p.spouse;
  if (s.kind === "person") {
    const partner = byKey.get(s.key);
    return partner?.id ? { spouse: { personnelId: partner.id } } : {};
  }
  if (s.kind === "new") {
    const firstName = s.firstName.trim();
    const lastName = s.lastName.trim();
    const email = s.email.trim();
    if (!firstName || !lastName) {
      errors.push(`${who}: enter the spouse’s first and last name.`);
      return {};
    }
    return {
      spouse: { firstName, lastName, ...(email ? { email } : {}) },
    };
  }
  return {};
}

/**
 * The lines a refused save shows, in the portal's own words (BRK-S279).
 *
 * A `VALIDATION_FAILED` that names a person's field (`personnel.2.email`) is
 * put against that person by name, because a roster of five with one
 * "isn’t valid" is otherwise a hunt. Everything else is `messageFor`.
 */
export function saveErrorLines(
  err: unknown,
  sent: AccountInput | null,
): string[] {
  const failures = validationFailures(err);
  const people = sent?.personnel ?? [];
  const lines: string[] = [];
  let unplaced = failures === null || failures.length === 0;
  for (const { path, reason } of failures ?? []) {
    const m = /^personnel\.(\d+)(?:\.|$)/.exec(path);
    const person = m ? people[Number(m[1])] : undefined;
    if (!person) {
      unplaced = true;
      continue;
    }
    const line = `${person.firstName} ${person.lastName}: ${validationReasonCopy(reason)}`;
    if (!lines.includes(line)) lines.push(line);
  }
  // Anything the lines above could not name still gets the summary.
  return unplaced ? [...lines, messageFor(err)] : lines;
}
